import { attempt } from "@omni/utils/function.utils";
import { isDefined } from "@omni/utils/helper.utils";
import { entries, fromEntries } from "@omni/utils/object.utils";
import { CSSProperties } from "react";

import BreakpointsService, {
    BreakpointMinSizes
} from "@/services/breakpoint.service";

export type LayoutSettings = Partial<
    Record<
        BreakpointMinSizes,
        {
            slidesPerView: number | "auto";
            slidesPerGroup?: number;
            direction?: "horizontal" | "vertical";
        }
    >
>;

export type EffectiveLayoutSettings = Record<
    number,
    {
        slidesPerView: number | "auto";
        slidesPerGroup?: number;
        direction?: "horizontal" | "vertical";
    }
>;

/**
 * Parse the settings to JSON
 * Replace the breakpoint aliases with the actual breakpoint values
 */
export const getEffectiveLayoutSettings = (
    originalSettings: LayoutSettings
): EffectiveLayoutSettings => {
    if (!originalSettings) return {};

    const parsedSettings =
        typeof originalSettings === "string"
            ? attempt<LayoutSettings | null>(
                  () => JSON.parse(originalSettings),
                  null
              )
            : originalSettings;

    if (!isDefined(parsedSettings)) return {};

    return fromEntries(
        entries(parsedSettings)
            .map(([breakpoint, value]) =>
                !isDefined(value) ? undefined : ([breakpoint, value] as const)
            )
            .filter(isDefined)
            .map(([breakpoint, value]) => [
                BreakpointsService.getBreakpointMinValues(breakpoint),
                value
            ])
    );
};

/**
 * Generate CSS variables for the layout settings
 *
 * @param props.layoutSettings The layout settings for the swiper
 * @param props.spaceBetween The space between the slides
 * @returns CSS properties with the layout settings
 */
export const getLayoutCssVariables = ({
    layoutSettings,
    spaceBetween
}: {
    layoutSettings: LayoutSettings;
    spaceBetween: number;
}) => {
    const xsSlides =
        layoutSettings[BreakpointMinSizes.xs]?.slidesPerView ?? 2.5;
    const smSlides =
        layoutSettings[BreakpointMinSizes.sm]?.slidesPerView ?? xsSlides;
    const mdSlides =
        layoutSettings[BreakpointMinSizes.md]?.slidesPerView ?? smSlides;
    const lgSlides =
        layoutSettings[BreakpointMinSizes.lg]?.slidesPerView ?? mdSlides;
    const xlSlides =
        layoutSettings[BreakpointMinSizes.xl]?.slidesPerView ?? lgSlides;

    return {
        "--slides-xs": xsSlides,
        "--slides-sm": smSlides,
        "--slides-md": mdSlides,
        "--slides-lg": lgSlides,
        "--slides-xl": xlSlides,
        "--slider-spacing": `${spaceBetween}px`
    } satisfies CSSProperties;
};
